import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  RouteComponentProps,
  Redirect,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './App.css';
import './theme.css';
import 'components/FormElements/Button/SubmitButton.css';
import 'components/Invoicing/invoicing.css';
import 'components/Invoicing/Configuration/UpdateCard/Ancillary/Ancillary.css';
import 'components/Invoicing/Configuration/UpdateCard/Transaction/TxnPricingTable.css';
import NotFoundErrorPage from 'routes/NotFoundErrorPage';
import storageService from 'services/storage.service';
import DashboardPagesLayout from 'components/layouts/DashboardPagesLayout';
import ReferralInvoicingRoute from 'routes/invoicing/ReferralInvoicing';
import AccessPagesLayout from './components/layouts/AccessPagesLayout';
import { asyncComponent } from './libs/helpers';
import OpttyLogo from './logo.png';

import { checkAuthenticated } from './services/auth.service';

const LoginPage = asyncComponent(() => import('routes/Login'));
const OnboardingMerchantPage = asyncComponent(() => import('routes/onboarding/OnboardingMerchant'));
const AuditLogPage = asyncComponent(() => import('routes/AuditLog'));
const OrderDetailsPage = asyncComponent(() => import('routes/OrderDetailsPage'));
const OnboardingParentPage = asyncComponent(() => import('routes/onboarding/OnboardingParent'));
const OnboardingGrandparentPage = asyncComponent(() => import('routes/onboarding/OnboardingGrandparent'));
const UserManagementPage = asyncComponent(() => import('routes/user-management/UserManagementPage'));
const InvoiceGeneratePage = asyncComponent(() => import('routes/invoicing/InvoiceGenerate'));
const CommercialModelPage = asyncComponent(() => import('routes/invoicing/CommercialModel'));
const ManageGrandParents = asyncComponent(() => import('routes/operations/ManageGrandParents'));
const ManageUserMenus = asyncComponent(() => import('routes/operations/ManageUserMenus'));
const ManageApmConfigurations = asyncComponent(() => import('routes/operations/ManageApmConfigurations'));
const ManageFeatureMatrix = asyncComponent(() => import('routes/operations/ManageFeatureMatrix'));
const AddFeatureMatrixPage = asyncComponent(() => import('routes/operations/AddFeatureMatrixPage'));
const FeatureMatrixDetailsPage = asyncComponent(() => import('routes/operations/FeatureMatrixDetailsPage'));
const ListFeatureMatrixPage = asyncComponent(() => import('routes/payment-methods/ListFeatureMatrixPage'));
const PMFeatureMatrixDetailPage = asyncComponent(() => import('routes/payment-methods/PMFeatureMatrixDetailPage'));
const PMFeatureMatrixFormPage = asyncComponent(() => import('routes/payment-methods/PMFeatureMatrixFormPage'));
const PaymentBrandFormPage = asyncComponent(() => import('routes/payment-methods/PaymentBrandFormPage'));
const PaymentConnectorFormPage = asyncComponent(() => import('routes/payment-methods/PaymentConnectorFormPage'));
const ManagePaymentMethods = asyncComponent(() => import('routes/operations/ManagePaymentMethods'));
const AnnouncementsPage = asyncComponent(() => import('routes/announcements/AnnouncementsPage'));
const AnnouncementTagsPage = asyncComponent(() => import('routes/announcements/AnnouncementTagsPage'));
const SelfHealingPage = asyncComponent(() => import('routes/self-healing/SelfHealingPage'));
const SelfHealingRunPage = asyncComponent(() => import('routes/self-healing/SelfHealingRunPage'));
const SelfHealingHowItWorksPage = asyncComponent(() => import('routes/self-healing/SelfHealingHowItWorksPage'));
const SelfHealingImpactPage = asyncComponent(() => import('routes/self-healing/SelfHealingImpactPage'));
const WebsiteContentPage = asyncComponent(() => import('routes/website-content/WebsiteContentPage'));
const WebsiteContentEditorPage = asyncComponent(() => import('routes/website-content/WebsiteContentEditorPage'));
const AddPaymentProviderPage = asyncComponent(() => import('routes/operations/AddPaymentProviderPage'));
const PaymentProviderDetailsPage = asyncComponent(() => import('routes/operations/PaymentProviderDetailsPage'));

function App() {
  const { i18n } = useTranslation();
  const [authenticated, setAuthenticated] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const jwt = storageService.getJwtCookie() || '';
    checkAuthenticated(jwt).then((data) => {
      setAuthenticated(data);
      setIsInitialized(true);
    });
  }, []);

  useEffect(() => {
    if (isInitialized) {
      i18n.changeLanguage('en');
    }
  }, [isInitialized]);

  if (!isInitialized) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
      }}
      >
        <img
          alt="Optty Logo"
          className="brand-logo"
          src={OpttyLogo}
        />
        Loading...
        <div style={{
          marginTop: '10px',
          width: '20px',
          height: '20px',
          backgroundColor: '#156cF8',
          animation: 'spin 5000ms linear infinite',
        }}
        />
      </div>
    );
  }

  return (
    <Router>
      <Switch>
        <Route path="/">
          {authenticated ? (
            <DashboardPagesLayout>
              <Switch>
                <Route
                  exact
                  path={['/', '/app', '/app/onboarding', '/app/onboarding/child']}
                  render={(routeProps: RouteComponentProps) => {
                    return <OnboardingMerchantPage {...routeProps} />;
                  }}
                />
                <Route
                  exact
                  path={['/app/onboarding/parent']}
                  render={(routeProps: RouteComponentProps) => {
                    return <OnboardingParentPage {...routeProps} />;
                  }}
                />
                <Route
                  exact
                  path={['/app/onboarding/grandparent']}
                  render={(routeProps: RouteComponentProps) => {
                    return <OnboardingGrandparentPage {...routeProps} />;
                  }}
                />
                <Route
                  exact
                  path={['/app/user-management']}
                  render={(routeProps: RouteComponentProps) => {
                    return <UserManagementPage {...routeProps} />;
                  }}
                />
                <Route
                  exact
                  path={['/app/auditLog']}
                  render={(routeProps: RouteComponentProps) => {
                    return <AuditLogPage {...routeProps} />;
                  }}
                />
                <Route
                  exact
                  path={['/app/order-details']}
                  render={(routeProps: RouteComponentProps) => {
                    return <OrderDetailsPage {...routeProps} />;
                  }}
                />
                <Route
                  exact
                  path={['/app/trans-details']}
                  render={(routeProps: RouteComponentProps) => {
                    return <OrderDetailsPage {...routeProps} />;
                  }}
                />
                <Route
                  exact
                  path={['/app/auditLog']}
                  render={(routeProps: RouteComponentProps) => {
                    return <AuditLogPage {...routeProps} />;
                  }}
                />
                {/* Redirect old /invoicing and /invoicing/details to the new Commercial Model page */}
                <Route
                  exact
                  path={['/app/invoicing', '/app/invoicing/details', '/app/invoicing/details/:familyId']}
                  render={() => <Redirect to="/app/invoicing/commercial-model" />}
                />
                <Route
                  exact
                  path={['/app/invoicing/generate']}
                  render={(routeProps: RouteComponentProps) => {
                    return <InvoiceGeneratePage {...routeProps} />;
                  }}
                />
                <Route
                  exact
                  path={['/app/invoicing/referralAgreement']}
                  render={() => <ReferralInvoicingRoute />}
                />
                <Route
                  exact
                  path={['/app/invoicing/commercial-model']}
                  render={() => <CommercialModelPage />}
                />
                <Route
                  exact
                  path={['/app/operations/ringfencing-grandparents']}
                  render={() => <ManageGrandParents />}
                />
                <Route
                  exact
                  path={['/app/operations/menu-management']}
                  render={() => <ManageUserMenus />}
                />
                <Route
                  exact
                  path={['/app/operations/apm-configurations']}
                  render={() => <ManageApmConfigurations />}
                />
                <Route
                  exact
                  path={['/app/operations/feature-matrix']}
                  render={() => <ManageFeatureMatrix />}
                />
                <Route
                  exact
                  path={['/app/operations/add-feature-matrix']}
                  render={() => <AddFeatureMatrixPage />}
                />
                <Route
                  exact
                  path={['/app/operations/feature-matrix/:providerId']}
                  render={() => <FeatureMatrixDetailsPage />}
                />
                <Route
                  exact
                  path={['/app/operations/edit-feature-matrix/:providerId', '/operations/edit-feature-matrix/:providerId']}
                  render={() => <AddFeatureMatrixPage />}
                />
                <Route
                  exact
                  path={['/app/operations/payment-methods', '/operations/payment-methods']}
                  render={() => <ManagePaymentMethods />}
                />
                <Route
                  exact
                  path={['/app/announcements']}
                  render={() => <AnnouncementsPage />}
                />
                <Route
                  exact
                  path={['/app/announcements/tags']}
                  render={() => <AnnouncementTagsPage />}
                />
                <Route
                  exact
                  path={['/app/payment-methods/feature-matrix']}
                  render={() => <ListFeatureMatrixPage />}
                />
                <Route
                  exact
                  path={['/app/payment-methods/feature-matrix/add']}
                  render={() => <PMFeatureMatrixFormPage />}
                />
                <Route
                  exact
                  path={['/app/payment-methods/feature-matrix/edit/:providerId']}
                  render={() => <PMFeatureMatrixFormPage />}
                />
                <Route
                  exact
                  path={['/app/payment-methods/feature-matrix/:providerId']}
                  render={() => <PMFeatureMatrixDetailPage />}
                />
                <Route
                  exact
                  path={['/app/payment-methods/payment-brands/add']}
                  render={() => <PaymentBrandFormPage />}
                />
                <Route
                  exact
                  path={['/app/payment-methods/payment-brands/edit/:brandId']}
                  render={() => <PaymentBrandFormPage />}
                />
                <Route
                  exact
                  path={['/app/payment-methods/payment-connectors/add']}
                  render={() => <PaymentConnectorFormPage />}
                />
                <Route
                  exact
                  path={['/app/payment-methods/payment-connectors/edit/:connectorId']}
                  render={() => <PaymentConnectorFormPage />}
                />
                <Route
                  exact
                  path={['/app/operations/add-payment-provider', '/operations/add-payment-provider']}
                  render={() => <AddPaymentProviderPage />}
                />
                <Route
                  exact
                  path={['/app/operations/payment-provider/:providerId', '/operations/payment-provider/:providerId']}
                  render={() => <PaymentProviderDetailsPage />}
                />
                <Route
                  exact
                  path={['/app/operations/edit-payment-provider/:providerId', '/operations/edit-payment-provider/:providerId']}
                  render={() => <AddPaymentProviderPage />}
                />
                <Route
                  exact
                  path={['/app/self-healing']}
                  render={(routeProps: RouteComponentProps) => (
                    <SelfHealingPage {...routeProps} />
                  )}
                />
                <Route
                  exact
                  path={['/app/self-healing/how-it-works']}
                  render={(routeProps: RouteComponentProps) => (
                    <SelfHealingHowItWorksPage {...routeProps} />
                  )}
                />
                <Route
                  exact
                  path={['/app/self-healing/impact']}
                  render={(routeProps: RouteComponentProps) => (
                    <SelfHealingImpactPage {...routeProps} />
                  )}
                />
                <Route
                  exact
                  path={['/app/self-healing/runs/:runId']}
                  render={(routeProps: RouteComponentProps) => (
                    <SelfHealingRunPage {...routeProps} />
                  )}
                />
                <Route
                  exact
                  path={['/app/website-content']}
                  render={(routeProps: RouteComponentProps) => (
                    <WebsiteContentPage {...routeProps} />
                  )}
                />
                <Route
                  exact
                  path={['/app/website-content/:type/new', '/app/website-content/:type/:itemId']}
                  render={(routeProps: RouteComponentProps) => (
                    <WebsiteContentEditorPage {...routeProps} />
                  )}
                />
              </Switch>
            </DashboardPagesLayout>
          ) : (
            <>
              <AccessPagesLayout>
                <Switch>
                  <Route
                    exact
                    path="/login"
                    render={(routeProps: RouteComponentProps) => {
                      return <LoginPage {...routeProps} />;
                    }}
                  />
                  <Route component={NotFoundErrorPage} />
                </Switch>
              </AccessPagesLayout>
              <Redirect to="/login" />
            </>
          )}
        </Route>
        <Route component={NotFoundErrorPage} />
      </Switch>
    </Router>
  );
}

export default App;
